import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LayoutGrid, User, ChevronRight, Users, X, Archive } from 'lucide-react';
import { orgService } from '../../services/orgService';
import { SupervisorDashboards } from './supervisorCharts';
import ClinicianFullView from './ClinicianFullView';
import SeatManagement from './SeatManagement';
import LoadingSpinner from '../UI/LoadingSpinner';

const CHART_TYPES = [
  'caseload-diagnosis', 'no-show-analysis', 'treatment-plan-review',
  'individual-phq9-tracker', 'individual-gad7-tracker'
];

/**
 * Practice administrator ("supervisor") view.
 * - Aggregate: all seated clinicians' analytics rolled into one org view.
 * - Drill-down: click a clinician to see just their five dashboards.
 * Analytics only — never note content. Revenue and Risk Level are excluded.
 */
const SupervisorDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');
  const [orgData, setOrgData] = useState(null);       // /org : { org, members }
  const [analytics, setAnalytics] = useState(null);   // /analytics : { orgName, clinicians }
  const [selected, setSelected] = useState(null);     // clinician email, or null = aggregate
  const [showSeats, setShowSeats] = useState(false);  // seat-management modal
  const [archived, setArchived] = useState([]);       // former clinicians (org-retained records)

  const load = async () => {
    try {
      const [org, an, arch] = await Promise.all([orgService.getOrg(), orgService.getAnalytics(), orgService.getArchived()]);
      if (!org.success || !an.success) throw new Error('Could not load organization data');
      setOrgData(org);
      setAnalytics(an);
      setArchived(arch.formerClinicians || []);
    } catch (err) {
      if (err.response?.status === 403) {
        // Not a practice administrator — send them home instead of a dead-end page.
        navigate('/', { replace: true });
        return;
      }
      setError(err.message || 'Failed to load supervisor data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === '1') {
      // Just returned from Stripe Checkout — the webhook creates the org
      // asynchronously. Poll until we're recognized as admin, then reload
      // once so the app picks up the Supervisor nav + fresh entitlement.
      setFinalizing(true);
      let tries = 0;
      const timer = setInterval(async () => {
        tries++;
        const me = await orgService.getMe();
        if (me?.role === 'admin') {
          clearInterval(timer);
          window.history.replaceState({}, '', '/supervisor');
          window.location.reload();
        } else if (tries >= 15) {
          clearInterval(timer);
          setFinalizing(false);
          setError('Your subscription is being set up — give it a moment and refresh.');
          setLoading(false);
        }
      }, 2000);
      return () => clearInterval(timer);
    }
    load();
  }, []);

  const clinicians = analytics?.clinicians || [];
  const formerClinicians = archived || [];

  // Build the data scope for the current view (aggregate vs one clinician)
  const scopeData = useMemo(() => {
    if (selected) {
      const c = clinicians.find(x => x.email === selected);
      return c ? c.charts : {};
    }
    const agg = {};
    CHART_TYPES.forEach(ct => { agg[ct] = clinicians.flatMap(c => (c.charts?.[ct]) || []); });
    return agg;
  }, [selected, clinicians]);

  if (finalizing) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24 gap-4">
        <LoadingSpinner size="large" />
        <p className="text-gray-600">Finalizing your Supervisor subscription…</p>
      </div>
    );
  }
  if (loading) {
    return <div className="flex items-center justify-center h-full py-24"><LoadingSpinner size="large" /></div>;
  }
  if (error) {
    return (
      <div className="max-w-xl mx-auto mt-16 bg-white rounded-2xl shadow p-8 text-center">
        <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-700">{error}</p>
      </div>
    );
  }

  const org = orgData?.org;
  const selectedClinician = selected
    ? (clinicians.find(c => c.email === selected) || formerClinicians.find(c => c.email === selected) || null)
    : null;
  const selectedIsFormer = selectedClinician ? !clinicians.some(c => c.email === selected) : false;

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-7 h-7 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">{org?.orgName || 'Your Practice'}</h1>
          <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">Supervisor</span>
        </div>
        <p className="text-lg font-semibold text-gray-700 mb-1">A practice-wide AI scribe and visual analytics infrastructure for your entire business.</p>
        <p className="text-sm text-gray-400 mb-6">Visual analytics only — no client records or note content.</p>

        {/* Manage seats — panel lives in a modal to keep the dashboard clean */}
        <div className="mb-8">
          <button
            onClick={() => setShowSeats(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-sm"
          >
            <Users className="w-4 h-4 text-blue-600" />
            Manage Seats
            <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{org?.seatsUsed ?? 0}/{org?.seatCount ?? 0}</span>
          </button>
        </div>

        {/* Scope selector: All (aggregate) + each clinician */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelected(null)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition ${
              !selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> All Clinicians (Aggregate)
          </button>
          {clinicians.map(c => (
            <button
              key={c.email}
              onClick={() => setSelected(c.email)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                selected === c.email ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <User className="w-4 h-4" /> {c.name}
              {!c.hasData && <span className="text-xs opacity-70">(no data)</span>}
            </button>
          ))}
        </div>

        {/* Former clinicians — records retained by the practice after seat removal */}
        {formerClinicians.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Former clinicians · records retained by your practice</p>
            <div className="flex flex-wrap gap-2">
              {formerClinicians.map(c => (
                <button
                  key={c.email}
                  onClick={() => setSelected(c.email)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                    selected === c.email ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Archive className="w-4 h-4" /> {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Scope breadcrumb */}
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-4">
          <span>{org?.orgName}</span>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium text-gray-800">
            {selectedClinician ? selectedClinician.name : `Aggregate · ${clinicians.length} clinicians`}
          </span>
        </div>

        {/* Former-clinician banner */}
        {selectedIsFormer && (
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 bg-gray-100 border border-gray-200 rounded-lg px-4 py-2">
            <Archive className="w-4 h-4 text-gray-500" />
            Former clinician — this record is retained by your practice. Their access was removed with their seat.
          </div>
        )}

        {/* Aggregate = practice-wide summary; drill-down = the clinician's own full analytics view */}
        {selectedClinician
          ? <ClinicianFullView clinician={selectedClinician} />
          : <SupervisorDashboards scopeData={scopeData} />}

        {/* Seat management modal */}
        {showSeats && (
          <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
            <div className="relative w-full max-w-2xl my-10">
              <button
                onClick={() => setShowSeats(false)}
                className="absolute -top-3 -right-3 z-10 bg-white rounded-full p-1.5 shadow border border-gray-200 text-gray-500 hover:text-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
              <SeatManagement org={org} members={orgData?.members} onChanged={load} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupervisorDashboard;
